import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  EmailIcon,
  FacebookIcon,
  FacebookShareButton,
  FacebookMessengerIcon,
  FacebookMessengerShareButton,
  RedditIcon,
  RedditShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  TwitterShareButton,
  TwitterIcon,
  WhatsappIcon,
  WhatsappShareButton,
} from 'react-share';
import { CustomizableLink } from '~components/common/Link';
import { store } from '~state/ReduxWrapper';

const isAppleDevice = () => {
  const ua = navigator.userAgent;
  if (
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  ) {
    return true;
  }
  return false;
};

function ShareCaption({ text }) {
  return (
    <figcaption className='float-right my-auto ml-4 text-left'>
      {text}
    </figcaption>
  );
}

ShareCaption.propTypes = {
  text: PropTypes.string.isRequired,
};

export function ShareButtons({
  description,
  sharePath,
  includeSocialNetworks,
  includeEmail,
  includeSms,
  smsCaption,
}) {
  const sourcing = useSelector((state) => state.sourcing);
  const origin =
    typeof window !== 'undefined'
      ? window.location.origin
      : 'https://www.voteamerica.org';
  const toolBaseUrl = `${origin}${sharePath}`;
  const shareUrl =
    typeof sourcing.embedPageUrl !== 'undefined'
      ? sourcing.embedPageUrl
      : toolBaseUrl;

  const isEmbed = typeof sourcing.embedPageUrl !== 'undefined';

  const clearForm = () => {
    store.dispatch({ type: 'PERSON_RESET' });
  };

  return (
    <div className='space-y-7 [&>button>svg]:rounded-full [&>button]:flex'>
      {includeEmail && (
        <>
          <button type='button' title='Share via email' aria-label='email'>
            <a
              href={`mailto:?body=${encodeURIComponent(
                description
              )}%20${shareUrl}`}
              title='Share via Email'
              target='_blank'
              rel='noopener'
              className='border-0'
              aria-label='Share via Email'
            >
              <EmailIcon className='rounded-full' />
            </a>
            <ShareCaption text='Email' />
          </button>
          <div className='divider' />
        </>
      )}

      {includeSocialNetworks && (
        <>
          <FacebookShareButton url={shareUrl}>
            <FacebookIcon />
            <ShareCaption text='Facebook' />
          </FacebookShareButton>
          <div className='divider' />
        </>
      )}

      {includeSocialNetworks && (
        <>
          <LinkedinShareButton url={shareUrl} title={description}>
            <LinkedinIcon />
            <ShareCaption text='LinkedIn' />
          </LinkedinShareButton>
          <div className='divider' />
        </>
      )}

      {includeSocialNetworks && (
        <>
          <FacebookMessengerShareButton url={shareUrl}>
            <FacebookMessengerIcon />
            <ShareCaption text='Messenger' />
          </FacebookMessengerShareButton>
          <div className='divider' />
        </>
      )}

      {includeSocialNetworks && (
        <>
          <RedditShareButton url={shareUrl} title={description}>
            <RedditIcon />
            <ShareCaption text='Reddit' />
          </RedditShareButton>
          <div className='divider' />
        </>
      )}

      {includeSms && (
        <>
          <button type='button' title='Share via Text' aria-label='text'>
            <a
              // SMS urls are formatted differently for Apple, using & instead of ?
              href={
                isAppleDevice
                  ? `sms:&body=${encodeURIComponent(description)}%20${shareUrl}`
                  : `sms:?body=${encodeURIComponent(description)}%20${shareUrl}`
              }
              title='Share via Text'
              className='border-0'
            >
              <div className='h-16 w-16'>
                <img
                  src='/img/messages-icon.png'
                  alt='A white message bubble icon on a green background.'
                  className='h-full w-full rounded-full'
                />
              </div>
            </a>
            <ShareCaption text={smsCaption} />
          </button>
          <div className='divider' />
        </>
      )}

      {includeSocialNetworks && (
        <>
          <TwitterShareButton url={shareUrl} title={description}>
            <TwitterIcon />
            <ShareCaption text='Twitter' />
          </TwitterShareButton>
          <div className='divider' />
        </>
      )}

      {includeSocialNetworks && (
        <>
          <WhatsappShareButton url={shareUrl} title={description}>
            <WhatsappIcon />
            <ShareCaption text='WhatsApp' />
          </WhatsappShareButton>
          <div className='divider' />
        </>
      )}

      <CustomizableLink
        href={shareUrl}
        elementType='Link'
        onClick={clearForm}
        target='_self'
        isExternal={isEmbed}
        className='block w-fit'
      >
        Complete a new form
      </CustomizableLink>
      <CustomizableLink
        href='/'
        elementType='Link'
        isExternal={isEmbed}
        className='block w-fit'
      >
        Return to the home page
      </CustomizableLink>
    </div>
  );
}

ShareButtons.propTypes = {
  description: PropTypes.string,
  sharePath: PropTypes.string.isRequired,
  includeSocialNetworks: PropTypes.bool,
  includeEmail: PropTypes.bool,
  includeSms: PropTypes.bool,
  smsCaption: PropTypes.string,
};

ShareButtons.defaultProps = {
  includeSocialNetworks: true,
  includeEmail: true,
  includeSms: true,
  smsCaption: 'Text',
};

function Share({ description, sharePath, tool }) {
  let smsCaption = 'Text';
  if (tool === 'futurevoter') {
    smsCaption =
      'Text your friends to let them know they can register to vote before they turn 18 in every state except North Dakota.';
  }

  return (
    <div className='w-full rounded p-6 md:p-12'>
      <ShareButtons
        description={description}
        sharePath={sharePath}
        includeSocialNetworks={tool !== 'futurevoter'}
        includeEmail={tool !== 'futurevoter'}
        smsCaption={smsCaption}
      />
    </div>
  );
}

Share.propTypes = {
  description: PropTypes.string,
  sharePath: PropTypes.string.isRequired,
  tool: PropTypes.string,
};

export default Share;

export function ShareTool({ location, description, sharePath, tool }) {
  const variant = location.state && location.state.variant;
  let message = 'Before you go, can you share with your friends?';

  if (variant === 'form-submitted') {
    message =
      "You're all done now. We've sent your form. But before you go, can you share with your friends?";
  } else if (variant === 'form-emailed') {
    message =
      "You're all done now. We've emailed you your form — check your email for next steps. But before you go, can you share with your friends?";
  } else if (variant === 'form-mail') {
    message =
      'Thanks for requesting that your form be mailed to you. Your form and stamped return envelope should arrive in 6-10 days. But before you go, can you share with your friends?';
  }

  return (
    <>
      <h1>Every share helps us reach 3 more voters.</h1>

      {message && <p>{message}</p>}
      <Share description={description} sharePath={sharePath} tool={tool} />
    </>
  );
}

ShareTool.propTypes = {
  location: PropTypes.object,
  description: PropTypes.string,
  sharePath: PropTypes.string.isRequired,
  tool: PropTypes.string,
};
